import React from 'react';
import { Calendar, FileText, Hand, Video } from 'lucide-react';
import {
  taskOriginDescription,
  taskOriginTone,
  taskSourceLabel,
  type NetworkingTaskRow,
} from '../../lib/networkingTasks';

const TONE_CLASSES: Record<string, string> = {
  booktime: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200 border-sky-200 dark:border-sky-800',
  calendly: 'bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200 border-violet-200 dark:border-violet-800',
  readai: 'bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-200 border-teal-200 dark:border-teal-800',
  manual: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-600',
  default: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-600',
};

function OriginIcon({ source }: { source: string }) {
  if (source === 'booktime') return <Calendar className="w-3 h-3 shrink-0" />;
  if (source === 'calendly') return <Video className="w-3 h-3 shrink-0" />;
  if (source === 'readai') return <FileText className="w-3 h-3 shrink-0" />;
  if (source === 'manual') return <Hand className="w-3 h-3 shrink-0" />;
  return null;
}

interface TaskOriginBadgeProps {
  task: Pick<NetworkingTaskRow, 'source' | 'metadata'>;
  /** Short pill (source only) vs full origin line */
  compact?: boolean;
  className?: string;
}

export default function TaskOriginBadge({ task, compact = false, className = '' }: TaskOriginBadgeProps) {
  const tone = taskOriginTone(task.source);
  const label = compact ? taskSourceLabel(task.source) : taskOriginDescription(task);
  const title = compact ? taskOriginDescription(task) : undefined;

  return (
    <span
      title={title}
      className={`inline-flex items-center gap-1 max-w-full px-2 py-0.5 rounded-md text-xs font-medium border ${TONE_CLASSES[tone] ?? TONE_CLASSES.default} ${className}`}
    >
      <OriginIcon source={task.source} />
      <span className="truncate">{label}</span>
    </span>
  );
}
